// Minimal ZIP builder for in-browser use — no external dependencies.
// Stores files uncompressed (STORE method) which is fast and sufficient
// for bundling SAS-downloaded blobs into a single archive.

interface ZipEntry {
  path: string;
  data: Uint8Array;
}

const SIGNATURE_LOCAL = 0x04034b50;
const SIGNATURE_CENTRAL = 0x02014b50;
const SIGNATURE_END = 0x06054b50;

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) {
    crc ^= byte;
    for (let j = 0; j < 8; j++) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeLocalFileHeader(
  view: DataView,
  offset: number,
  nameBytes: Uint8Array,
  entry: ZipEntry,
): number {
  const crc = crc32(entry.data);
  view.setUint32(offset, SIGNATURE_LOCAL, true);
  view.setUint16(offset + 4, 20, true);
  view.setUint16(offset + 6, 0, true);
  view.setUint16(offset + 8, 0, true);
  view.setUint16(offset + 10, 0, true);
  view.setUint16(offset + 12, 0, true);
  view.setUint32(offset + 14, crc, true);
  view.setUint32(offset + 18, entry.data.length, true);
  view.setUint32(offset + 22, entry.data.length, true);
  view.setUint16(offset + 26, nameBytes.length, true);
  view.setUint16(offset + 28, 0, true);

  const headerSize = 30;
  const target = new Uint8Array(view.buffer);
  target.set(nameBytes, offset + headerSize);
  target.set(entry.data, offset + headerSize + nameBytes.length);

  return headerSize + nameBytes.length + entry.data.length;
}

function writeCentralDirectoryEntry(
  view: DataView,
  offset: number,
  nameBytes: Uint8Array,
  entry: ZipEntry,
  localHeaderOffset: number,
): number {
  const crc = crc32(entry.data);
  view.setUint32(offset, SIGNATURE_CENTRAL, true);
  view.setUint16(offset + 4, 20, true);
  view.setUint16(offset + 6, 20, true);
  view.setUint16(offset + 8, 0, true);
  view.setUint16(offset + 10, 0, true);
  view.setUint16(offset + 12, 0, true);
  view.setUint16(offset + 14, 0, true);
  view.setUint32(offset + 16, crc, true);
  view.setUint32(offset + 20, entry.data.length, true);
  view.setUint32(offset + 24, entry.data.length, true);
  view.setUint16(offset + 28, nameBytes.length, true);
  view.setUint16(offset + 30, 0, true);
  view.setUint16(offset + 32, 0, true);
  view.setUint16(offset + 34, 0, true);
  view.setUint16(offset + 36, 0, true);
  view.setUint32(offset + 38, 0, true);
  view.setUint32(offset + 42, localHeaderOffset, true);

  const headerSize = 46;
  new Uint8Array(view.buffer).set(nameBytes, offset + headerSize);

  return headerSize + nameBytes.length;
}

export function buildZipBlob(entries: ZipEntry[]): Blob {
  const encoder = new TextEncoder();
  const nameBytesList = entries.map((e) => encoder.encode(e.path));

  let totalSize = 0;
  for (let i = 0; i < entries.length; i++) {
    const nameLen = nameBytesList[i]!.length;
    const dataLen = entries[i]!.data.length;
    totalSize += 30 + nameLen + dataLen;
    totalSize += 46 + nameLen;
  }
  totalSize += 22;

  const buffer = new ArrayBuffer(totalSize);
  const view = new DataView(buffer);
  const localOffsets: number[] = [];

  let pos = 0;
  for (let i = 0; i < entries.length; i++) {
    localOffsets.push(pos);
    pos += writeLocalFileHeader(view, pos, nameBytesList[i]!, entries[i]!);
  }

  const centralStart = pos;
  for (let i = 0; i < entries.length; i++) {
    pos += writeCentralDirectoryEntry(
      view,
      pos,
      nameBytesList[i]!,
      entries[i]!,
      localOffsets[i]!,
    );
  }
  const centralSize = pos - centralStart;

  view.setUint32(pos, SIGNATURE_END, true);
  view.setUint16(pos + 4, 0, true);
  view.setUint16(pos + 6, 0, true);
  view.setUint16(pos + 8, entries.length, true);
  view.setUint16(pos + 10, entries.length, true);
  view.setUint32(pos + 12, centralSize, true);
  view.setUint32(pos + 16, centralStart, true);
  view.setUint16(pos + 20, 0, true);

  return new Blob([buffer], { type: 'application/zip' });
}
